import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../Redux Store/actions";
import { supabase } from "../../supabaseClient";
import {
    Box, Typography, Paper, Tabs, Tab, Grid, IconButton, Tooltip,
    Collapse, InputAdornment, TextField, CircularProgress, Stack,
    Avatar, Button, LinearProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, Chip, Checkbox, Popover, useMediaQuery,
} from "@mui/material";
import { ArrowForward, CalendarToday, TrackChanges } from "@mui/icons-material";
import {
    PhoneCallback, PhoneInTalk, PhoneMissed,
    Refresh, ExpandMore, ExpandLess, Mic, Psychology, Search,
    PhoneEnabled, Close, PlayArrow, Pause, ContactPhone,
    PhoneForwarded, PersonSearch, CheckCircle,
    TrendingUp, AccessTime, Article, FilterList,
    FolderOpen, BusinessCenter, Groups,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import { makeCall } from "../../Services/SmartFloApis";
import CallLogsDashboard from "./CallLogsDashboard";
import ContactsTab from "./ContactsTab";
import CPTab from "./CPTab";
import FOSTab from "./FOSTab";

/* ── Fonts & animations ─────────────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800&family=Geist+Mono:wght@400;500&display=swap');
@keyframes fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
@keyframes ping   { 75%,100%{transform:scale(2);opacity:0} }
`;

/* ── Design tokens ──────────────────────────────────────────────────────── */
const C = {
    indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
    green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
    red: "#dc2626", redSoft: "#fef2f2", redBd: "#fecaca",
    amber: "#d97706", amberSoft: "#fffbeb", amberBd: "#fde68a",
    sky: "#0284c7", skySoft: "#f0f9ff", skyBd: "#bae6fd",
    page: "#f8fafc", white: "#ffffff", border: "#e2e8f0", border2: "#f1f5f9",
    txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
    sans: "'Geist', system-ui, sans-serif", mono: "'Geist Mono', monospace",
};

/* ── Status map ─────────────────────────────────────────────────────────── */
const STATUS_MAP = {
    answered: { label: "Answered", color: C.green, soft: C.greenSoft, bd: C.greenBd, Icon: CheckCircle },
    missed: { label: "Missed", color: C.red, soft: C.redSoft, bd: C.redBd, Icon: PhoneMissed },
    "not answered": { label: "Not Answered", color: C.amber, soft: C.amberSoft, bd: C.amberBd, Icon: PhoneMissed },
};
const getStatus = s => STATUS_MAP[s] || { label: s || "Unknown", color: C.txt4, soft: C.page, bd: C.border, Icon: PhoneInTalk };



/* ── Lead status map ────────────────────────────────────────────────────── */
const LEAD_STATUS_MAP = {
    "0": { label: "New", color: "#6366f1" },
    "1": { label: "Claimed", color: "#0284c7" },
    "2": { label: "In Progress", color: "#d97706" },
    "3": { label: "Site Visit", color: "#7c3aed" },
    "4": { label: "Negotiation", color: "#db2777" },
    "5": { label: "Booked", color: "#16a34a" },
    "6": { label: "Lost", color: "#dc2626" },
    "7": { label: "On Hold", color: "#94a3b8" },
};
const getLeadStatus = v => LEAD_STATUS_MAP[String(v)] || { label: String(v || "—"), color: C.txt4 };

/* ── Hierarchy helpers ──────────────────────────────────────────────────── */

/**
 * BFS walk: returns every descendant ID (including rootId itself).
 * Works purely on { userId, parentId } rows — no role titles needed.
 */
function getAllDescendantIds(all, rootId) {
    const seen = new Set(), q = [rootId];
    while (q.length) {
        const id = q.shift();
        if (seen.has(id)) continue;
        seen.add(id);
        all.filter(u => u.parentId === id).forEach(u => q.push(u.userId));
    }
    return [...seen];
}

/**
 * From a candidate list of IDs, keep only the LEAF ones.
 * A leaf = userId that never appears as someone else's parentId.
 * These are the only users who actually own CallLogs / Leads rows.
 */
function getLeafIds(all, candidateIds) {
    const parentIdSet = new Set(
        all.filter(u => u.parentId !== null).map(u => u.parentId)
    );
    return candidateIds.filter(id => !parentIdSet.has(id));
}

/* ── Score / avatar helpers ─────────────────────────────────────────────── */
const scoreColor = n => n >= 70 ? C.green : n >= 40 ? C.amber : C.red;
const scoreSoft = n => n >= 70 ? C.greenSoft : n >= 40 ? C.amberSoft : C.redSoft;

const AV_COLORS = ["#6366f1", "#0ea5e9", "#8b5cf6", "#ec4899", "#f59e0b", "#22c55e", "#ef4444"];
const avColor = n => {
    if (!n) return C.txt4;
    let h = 0;
    for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff;
    return AV_COLORS[Math.abs(h) % AV_COLORS.length];
};
const initials = n => {
    if (!n) return "?";
    const p = n.trim().split(" ");
    return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase();
};

/* ── Time helpers ───────────────────────────────────────────────────────── */
const PAGE_SIZE = 15;
const fmtSec = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const fmtDur = s => { if (!s) return "—"; const m = Math.floor(s / 60), sec = s % 60; return m ? `${m}m ${sec}s` : `${sec}s`; };
const timeAgo = ms => {
    if (!ms) return "";
    const m = Math.floor((Date.now() - ms) / 60000);
    if (m < 1) return "Just now"; if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`;
};
const fmtTime = ms => !ms ? "—" : new Date(ms).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

/* ── Styled components ──────────────────────────────────────────────────── */
const Page = styled(Box)({
    background: C.page, minHeight: "100vh", padding: "12px",
    "@media (min-width:600px)": { padding: "24px" },
    fontFamily: C.sans,
});
const Surface = styled(Paper)({
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,.05)", position: "relative", zIndex: 1,
});
const CallCard = styled(Paper)(({ sc }) => ({
    background: C.white, border: `1px solid ${C.border}`, borderLeft: `4px solid ${sc}`,
    borderRadius: 10, boxShadow: "0 1px 2px rgba(0,0,0,.04)",
    overflow: "hidden", animation: "fadeUp .18s ease both",
    transition: "box-shadow .15s, transform .15s", cursor: "pointer",
    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,.08)", transform: "translateY(-1px)" },
}));

const StatusBadge = ({ status }) => {
    const st = getStatus(status);
    const { Icon } = st;
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            px: { xs: "7px", sm: "10px" }, py: "3px", borderRadius: 20,
            background: st.soft, border: `1px solid ${st.bd}`,
            color: st.color, fontSize: { xs: 10, sm: 12 }, fontWeight: 700,
            fontFamily: C.sans, whiteSpace: "nowrap", flexShrink: 0,
        }}>
            <Icon sx={{ fontSize: { xs: 11, sm: 13 }, color: st.color }} />
            {st.label}
        </Box>
    );
};

/* ── Contact-type chip (calllogs.contactType) — CP / FOS only ───────────── */
const CONTACT_TYPE_META = {
    cp: { label: "CP", color: C.indigo, soft: C.indigoSoft, bd: C.indigoBd },
    fos: { label: "FOS", color: C.amber, soft: C.amberSoft, bd: C.amberBd },
};
const ContactTypeChip = ({ type }) => {
    const meta = CONTACT_TYPE_META[String(type || "").trim().toLowerCase()];
    if (!meta) return null;
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center",
            px: { xs: "7px", sm: "9px" }, py: "2px", borderRadius: 20,
            background: meta.soft, border: `1px solid ${meta.bd}`,
            color: meta.color, fontSize: { xs: 10, sm: 11 }, fontWeight: 700,
            fontFamily: C.sans, whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.5,
        }}>
            {meta.label}
        </Box>
    );
};

const SearchBox = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        background: C.white, borderRadius: 8, fontSize: 13.5, fontFamily: C.sans,
        "& fieldset": { borderColor: C.border },
        "&:hover fieldset": { borderColor: C.indigoBd },
        "&.Mui-focused fieldset": { borderColor: C.indigo, boxShadow: `0 0 0 3px ${C.indigoSoft}` },
        "& input::placeholder": { color: C.txt4, opacity: 1 },
    },
});

const TopTabs = styled(Tabs)({
    minHeight: 44, borderBottom: `1px solid ${C.border}`,
    "& .MuiTabs-indicator": { height: 3, background: `linear-gradient(90deg,${C.indigo},${C.indigo2})`, borderRadius: "3px 3px 0 0" },
    "& .MuiTab-root": {
        fontFamily: `${C.sans} !important`, fontWeight: 700, fontSize: 13.5,
        textTransform: "none", minHeight: 44, padding: "8px 18px",
        color: C.txt3, letterSpacing: ".1px",
        "&:hover": { color: C.indigo, background: alpha(C.indigo, 0.03) },
        "&.Mui-selected": { color: C.indigo },
    },
});

const PillTabs = styled(Tabs)({
    minHeight: 40, padding: 3, borderRadius: 10,
    background: "#f8fafc", border: `1px solid ${C.border}`,
    "& .MuiTabs-indicator": { display: "none" },
    "& .MuiTab-root": {
        fontFamily: `${C.sans} !important`, fontWeight: 600, fontSize: 12,
        textTransform: "none", minHeight: 34, padding: "5px 12px",
        margin: "0 2px", borderRadius: 8, color: C.indigo,
        background: "#eef2ff", border: `1px solid ${C.indigoBd}`,
        transition: "all .2s ease",
        "&:hover": { background: "#eef2ff", color: C.indigo, borderColor: C.indigoBd },
        "&.Mui-selected": { background: C.indigo, color: "#fff", borderColor: C.indigo, boxShadow: "0 4px 12px rgba(99,102,241,0.25)" },
    },
});

const CbBtn = styled(Button)({
    borderRadius: 7, textTransform: "none", fontFamily: `${C.sans} !important`,
    fontWeight: 600, fontSize: 11.5, padding: "4px 10px",
    border: `1.5px solid ${C.indigoBd}`, color: C.indigo, background: C.indigoSoft,
    whiteSpace: "nowrap", minWidth: 0,
    "&:hover": { background: C.indigo, color: "#fff", borderColor: C.indigo },
});

const GreenBtn = styled(Button)({
    borderRadius: 8, textTransform: "none", fontFamily: `${C.sans} !important`,
    fontWeight: 600, fontSize: 12, padding: "4px 14px",
    background: C.green, color: "#fff", whiteSpace: "nowrap",
    "&:hover": { background: "#15803d", boxShadow: `0 4px 14px ${alpha(C.green, .3)}` },
});

/* ── Filter config ──────────────────────────────────────────────────────── */
const FILTER_TYPES = [
    { key: "sm", label: "SM", icon: <BusinessCenter sx={{ fontSize: 14 }} /> },
    { key: "project", label: "Project", icon: <FolderOpen sx={{ fontSize: 14 }} /> },
    { key: "status", label: "Status", icon: <PhoneInTalk sx={{ fontSize: 14 }} /> },
    { key: "contactType", label: "Contact Type", icon: <Groups sx={{ fontSize: 14 }} /> },
    { key: "date", label: "Date", icon: <CalendarToday sx={{ fontSize: 14 }} /> },

];

const CALL_STATUS_OPTIONS = [
    { value: "answered", label: "Answered", color: C.green },
    { value: "missed", label: "Missed", color: C.red },
    { value: "not answered", label: "Not Answered", color: C.amber },
];

// Contact Type filter options (calllogs.contactType distinct values)
const CONTACT_TYPE_OPTIONS = [
    { value: "CP", label: "CP", color: C.indigo },
    { value: "FOS", label: "FOS", color: C.amber },
    { value: "VJOP", label: "VJOP", color: C.sky },
    { value: "Lead", label: "Lead", color: C.green },
    { value: "Other", label: "Other", color: C.txt3 },
];

const LEAD_STATUS_OPTIONS = Object.entries(LEAD_STATUS_MAP).map(([value, { label, color }]) => ({ value, label, color }));

/* ── FilterPanel ────────────────────────────────────────────────────────── */
function FilterPanel({ anchorEl, onClose, tab, filters, onApply, smOptions, projectOptions, isLeafUser }) {
    const open = Boolean(anchorEl);

    // Leaf users own only their own records — hide the SM filter for them
    const filterTypes = FILTER_TYPES.filter(ft => !(isLeafUser && ft.key === "sm") && !(ft.key === "contactType" && tab !== 0));

    const [activeType, setActiveType] = useState(filterTypes[0]?.key);
    const [localFilters, setLocalFilters] = useState(filters);
    const [smSearch, setSmSearch] = useState("");
    const [projSearch, setProjSearch] = useState("");


    useEffect(() => { setLocalFilters(filters); }, [filters, open]);
    useEffect(() => { setActiveType(filterTypes[0]?.key); }, [tab]);

    const toggle = (key, value) =>
        setLocalFilters(prev => {
            const arr = prev[key] || [];
            return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
        });

    const totalSelected = Object.values(localFilters).flat().length;
    const filteredSms = smOptions.filter(s => s.name?.toLowerCase().includes(smSearch.toLowerCase()));
    const filteredProjects = projectOptions.filter(p => p.projectName?.toLowerCase().includes(projSearch.toLowerCase()));

    const renderOptions = () => {
        if (activeType === "sm") return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <Box sx={{ px: 1.5, pb: 1 }}>
                    <TextField size="small" placeholder="Search agents…" value={smSearch}
                        onChange={e => setSmSearch(e.target.value)} fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: C.txt4 }} /></InputAdornment>, sx: { fontSize: 12.5, fontFamily: C.sans, borderRadius: 2, background: C.page, "& fieldset": { borderColor: C.border }, height: 32 } }}
                    />
                </Box>
                <Box sx={{ overflowY: "auto", maxHeight: 240 }}>
                    {filteredSms.length === 0
                        ? <Typography sx={{ px: 2, py: 2, fontSize: 12, color: C.txt4, textAlign: "center" }}>No agents found</Typography>
                        : filteredSms.map(sm => {
                            const checked = (localFilters.sm || []).includes(sm.userId);
                            return (
                                <Box key={sm.userId} onClick={() => toggle("sm", sm.userId)}
                                    sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.85, cursor: "pointer", background: checked ? C.indigoSoft : "transparent", "&:hover": { background: checked ? C.indigoSoft : C.page }, transition: "background .1s" }}>
                                    <Checkbox size="large" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo }, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400, fontFamily: C.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {sm.name}
                                    </Typography>
                                </Box>
                            );
                        })}
                </Box>
            </Box>
        );

        if (activeType === "project") return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <Box sx={{ px: 1.5, pb: 1 }}>
                    <TextField size="small" placeholder="Search projects…" value={projSearch}
                        onChange={e => setProjSearch(e.target.value)} fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: C.txt4 }} /></InputAdornment>, sx: { fontSize: 12.5, fontFamily: C.sans, borderRadius: 2, background: C.page, "& fieldset": { borderColor: C.border }, height: 32 } }}
                    />
                </Box>
                <Box sx={{ overflowY: "auto", maxHeight: 240 }}>
                    {filteredProjects.length === 0
                        ? <Typography sx={{ px: 2, py: 2, fontSize: 12, color: C.txt4, textAlign: "center" }}>No projects found</Typography>
                        : filteredProjects.map(proj => {
                            const checked = (localFilters.project || []).includes(proj.projectId);
                            return (
                                <Box key={proj.projectId} onClick={() => toggle("project", proj.projectId)}
                                    sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.85, cursor: "pointer", background: checked ? C.indigoSoft : "transparent", "&:hover": { background: checked ? C.indigoSoft : C.page }, transition: "background .1s" }}>
                                    <Checkbox size="large" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo }, flexShrink: 0 }} />
                                    <Box sx={{ width: 24, height: 24, borderRadius: 1.5, flexShrink: 0, background: alpha(C.indigo, 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <FolderOpen sx={{ fontSize: 12, color: C.indigo }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400, fontFamily: C.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {proj.projectName}
                                        </Typography>
                                        {proj.projectShortCode && <Typography sx={{ fontSize: 10, color: C.txt4, fontFamily: C.mono }}>{proj.projectShortCode}</Typography>}
                                    </Box>
                                </Box>
                            );
                        })}
                </Box>
            </Box>
        );

        if (activeType === "status") {
            const opts = tab === 0 ? CALL_STATUS_OPTIONS : LEAD_STATUS_OPTIONS;
            return (
                <Box sx={{ overflowY: "auto", maxHeight: 260 }}>
                    {opts.map(opt => {
                        const checked = (localFilters.status || []).includes(opt.value);
                        return (
                            <Box key={opt.value} onClick={() => toggle("status", opt.value)}
                                sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.85, cursor: "pointer", background: checked ? C.indigoSoft : "transparent", "&:hover": { background: checked ? C.indigoSoft : C.page }, transition: "background .1s" }}>
                                <Checkbox size="large" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo }, flexShrink: 0 }} />
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400, fontFamily: C.sans }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            );
        }

        if (activeType === "contactType") {
            return (
                <Box sx={{ overflowY: "auto", maxHeight: 260 }}>
                    {CONTACT_TYPE_OPTIONS.map(opt => {
                        const checked = (localFilters.contactType || []).includes(opt.value);
                        return (
                            <Box key={opt.value} onClick={() => toggle("contactType", opt.value)}
                                sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.85, cursor: "pointer", background: checked ? C.indigoSoft : "transparent", "&:hover": { background: checked ? C.indigoSoft : C.page }, transition: "background .1s" }}>
                                <Checkbox size="large" checked={checked} sx={{ p: 0, color: C.border, "&.Mui-checked": { color: C.indigo }, flexShrink: 0 }} />
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12.5, color: checked ? C.indigo : C.txt1, fontWeight: checked ? 600 : 400, fontFamily: C.sans }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            );
        }

        if (activeType === "date") {
            const setQuickRange = (type) => {
                const now = new Date();
                let from = new Date(now);
                const to = new Date(now);

                if (type === "7days") {
                    from.setDate(from.getDate() - 6);
                }

                if (type === "30days") {
                    from.setDate(from.getDate() - 29);
                }

                setLocalFilters(prev => ({
                    ...prev,
                    date: {
                        from: from.toISOString().split("T")[0],
                        to: to.toISOString().split("T")[0]
                    }
                }));
            };

            return (
                <Box sx={{ px: 2, py: 1 }}>
                    <Stack spacing={2}>

                        {/* 🔥 Quick Filters */}
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            sx={{
                                pb: 1,
                                rowGap: 1, // 👈 fixes uneven wrapping
                            }}
                        >
                            {[
                                { label: "Today", value: "today" },
                                { label: "Last 7 Days", value: "7days" },
                                { label: "Last 30 Days", value: "30days" },
                            ].map((item) => (
                                <Chip
                                    key={item.value}
                                    size="small"
                                    label={item.label}
                                    onClick={() => setQuickRange(item.value)}
                                    sx={{
                                        height: 24,
                                        fontSize: 11.5,
                                        fontWeight: 500,
                                        px: 1,
                                        borderRadius: "6px",
                                        background: "#f1f5f9",
                                        border: "1px solid #e2e8f0",
                                        "&:hover": {
                                            background: "#e2e8f0",
                                        }
                                    }}
                                />
                            ))}
                        </Stack>

                        {/* Manual Date */}
                        <Stack spacing={1.5}>
                            <TextField
                                type="date"
                                label="From Date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={localFilters.date?.from || ""}
                                onChange={(e) =>
                                    setLocalFilters(prev => ({
                                        ...prev,
                                        date: { ...prev.date, from: e.target.value }
                                    }))
                                }
                            />

                            <TextField
                                type="date"
                                label="To Date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={localFilters.date?.to || ""}
                                onChange={(e) =>
                                    setLocalFilters(prev => ({
                                        ...prev,
                                        date: { ...prev.date, to: e.target.value }
                                    }))
                                }
                            />
                        </Stack>

                    </Stack>
                </Box>
            );
        }
        return null;
    };

    return (
        <Popover open={open} anchorEl={anchorEl} onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ elevation: 0, sx: { mt: 0.75, borderRadius: 3, border: `1px solid ${C.border}`, boxShadow: "0 8px 32px rgba(0,0,0,.12)", overflow: "hidden", width: 420, fontFamily: C.sans } }}>

            {/* Header */}
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.page }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FilterList sx={{ fontSize: 15, color: C.indigo }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.txt1, fontFamily: C.sans }}>Filters</Typography>
                    {/* {totalSelected > 0 && (
                        <Box sx={{ px: 0.8, py: 0.1, borderRadius: 10, background: C.indigo, color: "#fff", fontSize: 11, fontWeight: 700 }}>
                            {totalSelected}
                        </Box>
                    )} */}
                </Stack>
                <IconButton size="small" onClick={onClose} sx={{ color: C.txt4 }}>
                    <Close sx={{ fontSize: 15 }} />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ display: "flex", minHeight: 280 }}>
                {/* Left nav */}
                <Box sx={{ width: 140, borderRight: `1px solid ${C.border}`, background: C.page, flexShrink: 0, pt: 0.5 }}>
                    {filterTypes.map(ft => {
                        const count = (localFilters[ft.key] || []).length;
                        const isActive = activeType === ft.key;
                        return (
                            <Box key={ft.key} onClick={() => setActiveType(ft.key)}
                                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, cursor: "pointer", background: isActive ? C.white : "transparent", borderLeft: isActive ? `3px solid ${C.indigo}` : "3px solid transparent", "&:hover": { background: isActive ? C.white : alpha(C.indigo, 0.04) }, transition: "all .12s" }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Box sx={{ color: isActive ? C.indigo : C.txt4 }}>{ft.icon}</Box>
                                    <Typography sx={{ fontSize: 12.5, fontFamily: C.sans, fontWeight: isActive ? 600 : 400, color: isActive ? C.indigo : C.txt2 }}>
                                        {ft.label}
                                    </Typography>
                                </Stack>
                                {count > 0 && (
                                    <Box sx={{ minWidth: 18, height: 18, borderRadius: 10, background: C.indigo, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", px: 0.5 }}>
                                        {count}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Right options */}
                <Box sx={{ flex: 1, pt: 1, pb: 0.5, minWidth: 0 }}>
                    <Typography sx={{ px: 1.5, pb: 0.75, fontSize: 10.5, fontWeight: 700, color: C.txt4, textTransform: "uppercase", letterSpacing: ".06em" }}>
                        {filterTypes.find(f => f.key === activeType)?.label}
                    </Typography>
                    {renderOptions()}
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.page, gap: 1 }}>
                <Button size="small" onClick={() => setLocalFilters({ sm: [], project: [], status: [], contactType: [], date: { from: null, to: null } })}
                    sx={{ fontSize: 12, color: C.txt3, textTransform: "none", fontFamily: C.sans }}>
                    Clear all
                </Button>
                <Stack direction="row" spacing={0.75}>
                    <Button size="small" onClick={onClose}
                        sx={{ fontSize: 12, color: C.txt3, textTransform: "none", fontFamily: C.sans, border: `1px solid ${C.border}`, borderRadius: 2, px: 1.5 }}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={() => { onApply(localFilters); onClose(); }}
                        sx={{ fontSize: 12, textTransform: "none", fontFamily: C.sans, fontWeight: 600, background: C.indigo, borderRadius: 2, px: 2, "&:hover": { background: C.indigo2 } }}>
                        Apply
                    </Button>
                </Stack>
            </Box>
        </Popover>
    );
}

/* ── AudioPlayer ────────────────────────────────────────────────────────── */
function AudioPlayer({ url }) {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [prog, setProg] = useState(0);
    const [dur, setDur] = useState(0);
    const toggle = e => {
        e.stopPropagation();
        if (!ref.current) return;
        if (playing) { ref.current.pause(); setPlaying(false); }
        else { ref.current.play(); setPlaying(true); }
    };
    return (
        <Box onClick={e => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <audio ref={ref} src={url}
                onTimeUpdate={() => setProg(ref.current ? (ref.current.currentTime / (ref.current.duration || 1)) * 100 : 0)}
                onLoadedMetadata={() => setDur(ref.current?.duration || 0)}
                onEnded={() => { setPlaying(false); setProg(0); }} />
            <IconButton size="small" onClick={toggle} sx={{ width: 30, height: 30, background: C.indigo, color: "#fff", flexShrink: 0, "&:hover": { background: C.indigo2 } }}>
                {playing ? <Pause sx={{ fontSize: 13 }} /> : <PlayArrow sx={{ fontSize: 13 }} />}
            </IconButton>
            <Box sx={{ flex: 1, height: 3, background: C.border2, borderRadius: 3, cursor: "pointer", overflow: "hidden" }}
                onClick={e => { e.stopPropagation(); if (!ref.current) return; const r = e.currentTarget.getBoundingClientRect(); ref.current.currentTime = ((e.clientX - r.left) / r.width) * ref.current.duration; }}>
                <Box sx={{ height: "100%", width: `${prog}%`, background: C.indigo, borderRadius: 3, transition: "width .1s" }} />
            </Box>
            <Typography sx={{ fontSize: 11, color: C.txt4, minWidth: 32, fontFamily: C.mono }}>{dur ? fmtSec(dur) : "···"}</Typography>
        </Box>
    );
}

/* ── CallRow ────────────────────────────────────────────────────────────── */
function CallRow({ log, onCallback, setAiModal }) {
    const [open, setOpen] = useState(false);
    const st = getStatus(log.callStatus);
    const hasTranscript = !!log.transcription;

    // Talk ratio - use from log or default
    const agentRatio = log.agentTalkRatio ?? 78;
    const customerRatio = 100 - agentRatio;

    // Key talking points - array from log
    const talkingPoints = log.keyTalkingPoints || [];

    // Sentiment from log
    const sentiment = log.sentiment || "Neutral sentiment";
    const sentimentColor = 
        sentiment.toLowerCase().includes("positive") ? { bg: "#e8f7ee", fg: "#1a8a42", bd: "#b8e6c8" } :
        sentiment.toLowerCase().includes("negative") ? { bg: "#fdecec", fg: "#c23838", bd: "#f5c5c5" } :
        { bg: "#f3f1ff", fg: "#5a4fcf", bd: "#dcd6ff" };

    return (
        <CallCard elevation={0} sc={st.color} onClick={() => setOpen(p => !p)}>
            {/* ===== HEADER (Always visible) ===== */}
            <Box sx={{ px: { xs: 1.75, sm: 2.25 }, pt: { xs: 1.75, sm: 1.75 }, pb: { xs: 1.75, sm: 1.75 } }}>
                {/* Row 1: Name + ID + Status Badge */}
                <Box sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: { xs: 1.25, sm: 1 }
                }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1, flexWrap: "wrap" }}>
                        <Typography sx={{
                            fontWeight: 700,
                            fontSize: { xs: 14, sm: 15 },
                            color: C.txt1,
                            fontFamily: C.sans,
                            lineHeight: 1.3
                        }}>
                            {log.customerName || "Unknown"}
                        </Typography>
                        {log.leadDisplayId && (
                            <Typography sx={{
                                fontSize: { xs: 10.5, sm: 11 },
                                color: C.txt4,
                                fontFamily: C.mono,
                                flexShrink: 0,
                                mt: 0.2
                            }}>
                                #VJ{log.leadDisplayId}
                            </Typography>
                        )}
                        <ContactTypeChip type={log.contactType} />
                    </Stack>
                    <StatusBadge status={log.callStatus} />
                </Box>

                {/* Row 2: Call Type + Agent (pill + name) */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mb: { xs: 1.25, sm: 1 }
                }}>
                    {log?.aiCallType && (
                        <Box sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: "10px",
                            py: "3px",
                            borderRadius: 20,
                            fontSize: { xs: 10.5, sm: 11 },
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            background: C.indigo,
                            color: "white",
                            fontFamily: C.sans,
                            flexShrink: 0,
                            letterSpacing: ".01em"
                        }}>
                            {log.aiCallType}
                        </Box>
                    )}
                    {log?.agentName && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: C.indigoSoft,
                                color: C.indigo,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 9,
                                fontWeight: 700,
                                fontFamily: C.sans,
                                flexShrink: 0
                            }}>
                                {log.agentName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                            </Box>
                            <Typography sx={{ 
                                fontSize: { xs: 12, sm: 12.5 }, 
                                color: C.txt2, 
                                fontFamily: C.sans,
                                fontWeight: 500
                            }}>
                                {log.agentName}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Row 3: Time + Duration (left) | Callback + Chevron (right) */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    rowGap: { xs: 0.75, sm: 0 }
                }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap" }}>
                        <Stack direction="row" spacing={0.4} alignItems="center">
                            <AccessTime sx={{ fontSize: { xs: 11, sm: 12 }, color: C.txt4 }} />
                            <Typography sx={{ 
                                fontSize: { xs: 11, sm: 12 }, 
                                color: C.txt4, 
                                fontFamily: C.sans 
                            }}>
                                {timeAgo(log.startedAt)}
                            </Typography>
                        </Stack>
                        {log.durationSec > 0 && (
                            <>
                                <Typography sx={{ fontSize: 11, color: C.txt4 }}>·</Typography>
                                <Typography sx={{ 
                                    fontSize: { xs: 11, sm: 12 }, 
                                    color: C.txt4, 
                                    fontFamily: C.sans 
                                }}>
                                    {fmtDur(log.durationSec)}
                                </Typography>
                            </>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Box onClick={e => e.stopPropagation()}>
                            <CbBtn 
                                startIcon={<PhoneCallback sx={{ fontSize: 14 }} />} 
                                onClick={() => onCallback(log)}
                                sx={{ 
                                    px: { xs: "10px", sm: "14px" },
                                    py: "5px",
                                    fontSize: { xs: 11.5, sm: 12 },
                                    fontWeight: 600
                                }}
                            >
                                Callback
                            </CbBtn>
                        </Box>
                        <IconButton 
                            size="small" 
                            sx={{ 
                                color: C.txt4, 
                                p: 0.5,
                                "&:hover": { color: C.indigo, background: C.indigoSoft } 
                            }}
                        >
                            {open ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Stack>
                </Box>
            </Box>

            {/* ===== EXPANDED CONTENT ===== */}
            <Collapse in={open} unmountOnExit>
                <Box 
                    sx={{ 
                        background: C.page, 
                        px: { xs: 1.5, sm: 2.25 }, 
                        py: { xs: 1.5, sm: 2 }
                    }} 
                    onClick={e => e.stopPropagation()}
                >
                    {/* AI SUMMARY CARD */}
                    {log.aiShortSummary && (
                        <Box sx={{ 
                            background: C.white, 
                            border: `1px solid ${C.border}`, 
                            borderRadius: 2, 
                            p: { xs: 1.5, sm: 2 },
                            mb: 2
                        }}>
                            <Box sx={{ 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 1
                            }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Box sx={{ 
                                        width: 24, 
                                        height: 24, 
                                        borderRadius: "50%", 
                                        background: C.indigoSoft,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}>
                                        <Psychology sx={{ fontSize: 14, color: C.indigo }} />
                                    </Box>
                                    <Typography sx={{ 
                                        fontSize: { xs: 10.5, sm: 11 }, 
                                        fontWeight: 700, 
                                        color: C.indigo, 
                                        textTransform: "uppercase", 
                                        letterSpacing: ".08em" 
                                    }}>
                                        AI Summary
                                    </Typography>
                                </Stack>
                                {/* <Box sx={{
                                    px: "10px",
                                    py: "3px",
                                    borderRadius: 20,
                                    fontSize: { xs: 10, sm: 10.5 },
                                    fontWeight: 600,
                                    background: sentimentColor.bg,
                                    color: sentimentColor.fg,
                                    border: `1px solid ${sentimentColor.bd}`
                                }}>
                                    {sentiment}
                                </Box> */}
                            </Box>
                            <Typography sx={{ 
                                fontSize: { xs: 12.5, sm: 13 }, 
                                color: C.txt2, 
                                lineHeight: 1.65, 
                                fontFamily: C.sans, 
                                wordBreak: "break-word" 
                            }}>
                                {log.aiShortSummary}
                            </Typography>
                        </Box>
                    )}

                    {/* KEY TALKING POINTS */}
                    {talkingPoints.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <TrackChanges sx={{ fontSize: 14, color: C.txt3 }} />
                                <Typography sx={{ 
                                    fontSize: { xs: 10.5, sm: 11 }, 
                                    fontWeight: 700, 
                                    color: C.txt3, 
                                    textTransform: "uppercase", 
                                    letterSpacing: ".08em" 
                                }}>
                                    Key Talking Points
                                </Typography>
                            </Stack>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                {talkingPoints.map((point, idx) => (
                                    <Box key={idx} sx={{
                                        px: "11px",
                                        py: "5px",
                                        borderRadius: 20,
                                        fontSize: { xs: 11, sm: 11.5 },
                                        fontWeight: 500,
                                        background: C.white,
                                        color: C.txt2,
                                        border: `1px solid ${C.border}`,
                                        fontFamily: C.sans,
                                        whiteSpace: "nowrap"
                                    }}>
                                        {point}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* METRICS GRID - 4 cards */}
                    <Grid container spacing={{ xs: 1.25, sm: 1.5 }} sx={{ mb: 2.5 }}>
                        {[
                            ["Called", fmtTime(log.startedAt), AccessTime, false],
                            ["Ended", fmtTime(log.endedAt), AccessTime, false],
                            ["Duration", fmtDur(log.durationSec), AccessTime, false],
                            ["Lead Status", log.leadStatusSnapshot || "—", TrendingUp, true],
                        ].map(([l, v, Icon, highlight]) => (
                            <Grid item xs={6} sm={3} key={l} pb={2}>
                                <Box sx={{
                                    background: highlight ? C.indigoSoft : C.white,
                                    border: `1px solid ${highlight ? C.indigoBd : C.border}`,
                                    borderRadius: 2,
                                    px: { xs: 1.5, sm: 1.75 },
                                    py: { xs: 1.25, sm: 1.5 },
                                    height: "100%",
                                    pb:2
                                }}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: { xs: 0.75, sm: 0.5 } }}>
                                        <Icon sx={{ fontSize: 11, color: highlight ? C.indigo : C.txt4 }} />
                                        <Typography sx={{
                                            fontSize: { xs: 9.5, sm: 10 },
                                            color: highlight ? C.indigo : C.txt4,
                                            textTransform: "uppercase",
                                            letterSpacing: ".08em",
                                            fontWeight: 600
                                        }}>
                                            {l}
                                        </Typography>
                                    </Stack>
                                    <Typography sx={{
                                        fontSize: { xs: 12.5, sm: 13 },
                                        color: C.txt1,
                                        fontWeight: 700,
                                        wordBreak: "break-word",
                                        lineHeight: 1.3
                                    }}>
                                        {v}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

               

                    {/* CALL RECORDING */}
                    {log.recordingUrl && (
                        <Box sx={{ 
                            background: C.white, 
                            border: `1px solid ${C.border}`, 
                            borderRadius: 2, 
                            p: { xs: 1.25, sm: 1.5 }, 
                            mb: 2 
                        }}>
                            <Stack 
                                direction="row" 
                                spacing={0.75} 
                                alignItems="center" 
                                justifyContent="space-between"
                                sx={{ mb: 1 }}
                            >
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Box sx={{ 
                                        width: 24, 
                                        height: 24, 
                                        borderRadius: "50%", 
                                        background: C.indigoSoft, 
                                        display: "flex", 
                                        alignItems: "center", 
                                        justifyContent: "center" 
                                    }}>
                                        <Mic sx={{ fontSize: 13, color: C.indigo }} />
                                    </Box>
                                    <Typography sx={{ 
                                        fontSize: { xs: 10.5, sm: 11 }, 
                                        fontWeight: 700, 
                                        color: C.indigo, 
                                        textTransform: "uppercase", 
                                        letterSpacing: ".08em" 
                                    }}>
                                        Call Recording
                                    </Typography>
                                </Stack>
                            </Stack>
                            <AudioPlayer url={log.recordingUrl} />
                        </Box>
                    )}

                    {/* TRANSCRIPTION */}
                    {log.transcription && (
                        <Box sx={{ 
                            background: C.white, 
                            border: `1px solid ${C.border}`, 
                            borderRadius: 2, 
                            p: { xs: 1.25, sm: 1.5 },
                            mb: 2
                        }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <Box sx={{ 
                                    width: 24, 
                                    height: 24, 
                                    borderRadius: "50%", 
                                    background: C.border2, 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center" 
                                }}>
                                    <Article sx={{ fontSize: 13, color: C.txt3 }} />
                                </Box>
                                <Typography sx={{ 
                                    fontSize: { xs: 10.5, sm: 11 }, 
                                    fontWeight: 700, 
                                    color: C.txt3, 
                                    textTransform: "uppercase", 
                                    letterSpacing: ".08em" 
                                }}>
                                    Transcription
                                </Typography>
                            </Stack>
                            <Typography sx={{ 
                                fontSize: { xs: 12, sm: 13 }, 
                                color: C.txt2, 
                                lineHeight: 1.75, 
                                wordBreak: "break-word" 
                            }}>
                                {log.transcription}
                            </Typography>
                        </Box>
                    )}

                    {/* VIEW DETAILED ANALYSIS BUTTON */}
                    {log.aiDetailedSummary && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                onClick={() => setAiModal({ open: true, data: log })}
                                endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                                sx={{
                                    textTransform: "none",
                                    fontSize: { xs: 12, sm: 13 },
                                    fontWeight: 600,
                                    px: { xs: 2, sm: 2.5 },
                                    py: { xs: 0.75, sm: 1 },
                                    borderRadius: 2,
                                    color: "#fff",
                                    background: C.indigo,
                                    boxShadow: "none",
                                    "&:hover": {
                                        background: C.indigo2 || "#4c3fc0",
                                        boxShadow: "0 4px 12px rgba(90, 79, 207, 0.25)"
                                    }
                                }}
                            >
                                View Detailed Analysis
                            </Button>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </CallCard>
    );
}

/* ── LeadRow ────────────────────────────────────────────────────────────── */
function LeadRow({ lead, onCallNow }) {
    const person = lead.Person || {}, user = lead.Users || {};
    const name = person.name || "Unknown";
    const ls = getLeadStatus(lead.resolvedLeadStatus);
    return (
        <Surface elevation={0} sx={{ borderRadius: 4, overflow: "hidden" }}>
            <Box sx={{ px: { xs: 1.25, sm: 2 }, py: { xs: 1.25, sm: 1.4 }, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 1, sm: 1.5 } }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                    <Avatar sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, background: avColor(name), fontSize: { xs: 11, sm: 12 }, fontWeight: 700, flexShrink: 0 }}>
                        {initials(name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 13.5 }, color: C.txt1, fontFamily: C.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {name}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography sx={{ fontSize: 11, color: C.txt4, fontFamily: C.mono }}>
                                #VJ{lead.leadDisplayId || "—"}
                            </Typography>
                            {lead.projectName && (
                                <Typography sx={{ fontSize: 11, color: C.txt3, fontFamily: C.sans, display: { xs: "none", sm: "block" } }}>
                                    · {lead.projectName}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ px: "8px", py: "3px", borderRadius: 20, fontSize: { xs: 10.5, sm: 11 }, fontWeight: 600, whiteSpace: "nowrap", background: alpha(ls.color, .08), border: `1px solid ${alpha(ls.color, .2)}`, color: ls.color }}>
                            {ls.label}
                        </Box>
                        <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "right", minWidth: 90 }}>
                            <Typography sx={{ fontSize: 12, color: C.txt2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>
                                {user.name || <span style={{ color: C.txt4 }}>Unassigned</span>}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: C.txt4, fontFamily: C.mono }}>{timeAgo(lead.created_at)}</Typography>
                        </Box>
                    </Stack>
                    <GreenBtn startIcon={<PhoneForwarded sx={{ fontSize: 13 }} />} onClick={() => onCallNow(lead, person)} sx={{ fontSize: { xs: 11.5, sm: 12 }, px: { xs: "10px", sm: "14px" } }}>
                        Call Now
                    </GreenBtn>
                </Stack>
            </Box>
        </Surface>
    );
}

/* ── CallNowDialog ──────────────────────────────────────────────────────── */
function CallNowDialog({ open, lead, person, onClose, onConfirm }) {
    const name = person?.name || "Unknown";
    const phone = String(person?.contactNumber || "");
    const alt = person?.alternateNumber ? String(person.alternateNumber) : null;
    const [sel, setSel] = useState(phone);
    useEffect(() => { setSel(phone); }, [phone]);
    if (!lead) return null;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: { xs: 3, sm: 5 }, boxShadow: "0 20px 60px rgba(0,0,0,.14)", mx: { xs: 1.5, sm: "auto" } } }}>
            <DialogTitle sx={{ fontFamily: C.sans, fontWeight: 700, fontSize: 15, color: C.txt1, pb: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 36, height: 36, background: avColor(name), fontSize: 12, fontWeight: 700 }}>{initials(name)}</Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.txt1 }}>{name}</Typography>
                        <Typography sx={{ fontSize: 11, color: C.txt4, fontFamily: C.mono }}>#VJ{lead.leadDisplayId}</Typography>
                    </Box>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Stack spacing={1}>
                    {[{ label: "Primary", number: phone }, ...(alt ? [{ label: "Alternate", number: alt }] : [])].map(opt => (
                        <Box key={opt.number} onClick={() => setSel(opt.number)}
                            sx={{ border: `1.5px solid ${sel === opt.number ? C.indigo : C.border}`, borderRadius: 3, p: 1.25, cursor: "pointer", background: sel === opt.number ? C.indigoSoft : C.page, transition: "all .12s", "&:hover": { borderColor: C.indigoBd } }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PhoneEnabled sx={{ fontSize: 15, color: sel === opt.number ? C.indigo : C.txt4 }} />
                                <Typography sx={{ fontSize: 13, color: C.txt1, fontFamily: C.mono, fontWeight: 500 }}>
                                    {opt.number ? `${opt.number.slice(0, 2)}XXXXXX${opt.number.slice(-2)}` : ""}
                                </Typography>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ textTransform: "none", fontFamily: C.sans, color: C.txt3 }}>Cancel</Button>
                <GreenBtn startIcon={<PhoneForwarded sx={{ fontSize: 13 }} />} onClick={() => onConfirm(lead, person, sel)} sx={{ px: 3 }}>
                    Call
                </GreenBtn>
            </DialogActions>
        </Dialog>
    );
}

/* ════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════════════ */
export default function CallLogs() {
    const dispatch = useDispatch();
    const lu = useSelector(s => s?.Auth?.user);
    const callLogsShowMode = lu?.roleId?.accessibleTabs?.CallLogs?.["Show CallLogs"]?.show;
    const loggedInUserId = lu?.userId;
    console.log("Logged-in user:", lu);
    const agentMobile = lu?.contactNumber;
    const callerId = lu?.assignedCallerDid;

    /*
     * Tree-position flags — ZERO hardcoded role titles.
     *
     *  isRootUser  → user has no parentId  → top of the entire org → sees everything
     *  isLeafUser  → user has no children  → bottom agent          → sees only self
     *  (neither)   → mid-level manager     → sees leaf descendants
     *
     * visibleSmIds stores the LEAF userId array that scopes every DB query.
     *   null      = still resolving (show loading state)
     *   []        = root user (isRootUser = true, no .in() filter needed)
     *   [id, ...] = specific leaf agents this user can see
     */
    const [isRootUser, setIsRootUser] = useState(false);
    const [isLeafUser, setIsLeafUser] = useState(false);
    const [visibleSmIds, setVisibleSmIds] = useState(null);

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [activeFilters, setActiveFilters] = useState({ sm: [], project: [], status: [], contactType: [], date: { from: null, to: null } });
    const [smOptions, setSmOptions] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [aiModal, setAiModal] = useState({
        open: false,
        data: null
    });

    /* ── Step 1: Resolve tree position ────────────────────────────────── */
    useEffect(() => {
        // console.log("Resolving user hierarchy for userId:", loggedInUserId);
        if (!loggedInUserId) return;
        (async () => {
            try {
                console.log("Fetching all users for hierarchy resolution...");
                const { data, error } = await supabase
                    .from("Users")
                    .select("userId, parentId");
                if (error) throw error;

                const me = data.find(u => u.userId === loggedInUserId);
                const hasParent = me?.parentId !== null && me?.parentId !== undefined;
                const hasChildren = data.some(u => u.parentId === loggedInUserId);

                if (!hasParent) {
                    // Root of the entire org — no userId filter, sees everything
                    console.log("User is root of the org");
                    setIsRootUser(true);
                    setIsLeafUser(false);
                    setVisibleSmIds([]); // [] + isRootUser=true → applyVis returns query unfiltered
                    return;
                }

                if (!hasChildren) {
                    // Leaf agent — scoped to their own records only
                    console.log("User is a leaf agent");
                    setIsRootUser(false);
                    setIsLeafUser(true);
                    setVisibleSmIds([loggedInUserId]);
                    return;
                }

                // Mid-level manager — collect all descendants, then keep only leaves.
                // CallLogs / Leads rows carry the userId of the LEAF agent who made/owns
                // them, never a manager's userId. So we must filter down to leaves.
                const allDescendants = getAllDescendantIds(data, loggedInUserId);
                const leafIds = getLeafIds(data, allDescendants);
                console.log("allDescendants:", allDescendants);
                console.log("leafIds:", leafIds);
                setIsRootUser(false);
                setIsLeafUser(false);
                setVisibleSmIds(leafIds.length > 0 ? leafIds : [loggedInUserId]);

            } catch (e) {
                // Safe fallback
                console.error("Hierarchy resolve error:", e);
                setIsRootUser(false);
                setIsLeafUser(true);
                setVisibleSmIds([loggedInUserId]);
            }
        })();
    }, [loggedInUserId, callLogsShowMode]);

    /* ── Step 2: SM options for filter panel (always leaf users only) ── */
    useEffect(() => {
        if (visibleSmIds === null) return;
        (async () => {
            try {
                const { data, error } = await supabase
                    .from("Users")
                    .select("userId, name, parentId");
                if (error) throw error;

                const parentIdSet = new Set(
                    data.filter(u => u.parentId !== null).map(u => u.parentId)
                );

                const leafUsers = isRootUser
                    // Root sees ALL leaf users across the entire org
                    ? data.filter(u => !parentIdSet.has(u.userId))
                    // Everyone else sees only leaves within their visible set
                    : (() => {
                        const visibleSet = new Set(visibleSmIds);
                        return data.filter(u => visibleSet.has(u.userId) && !parentIdSet.has(u.userId));
                    })();

                setSmOptions(leafUsers);
            } catch (e) { console.error(e); }
        })();
    }, [visibleSmIds, isRootUser]);

    /* ── Step 3: Projects (global, no scoping needed) ──────────────────── */
    useEffect(() => {
        (async () => {
            try {
                const { data } = await supabase.from("Projects").select("projectId,projectName,projectShortCode").eq("isVJ", true);
                if (data) setProjectOptions(data);
            } catch (e) { console.error(e); }
        })();
    }, []);

    /* ── Tabs / pagination state ────────────────────────────────────────── */
    const [viewTab, setViewTab] = useState(0);
    const [tab, setTab] = useState(0);

    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsHasMore, setLogsHasMore] = useState(true);
    const [logsPage, setLogsPage] = useState(0);
    const logsSentinel = useRef(null), logsObs = useRef(null);

    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [leadsHasMore, setLeadsHasMore] = useState(true);
    const [leadsPage, setLeadsPage] = useState(0);
    const leadsSentinel = useRef(null), leadsObs = useRef(null);

    const [qi, setQi] = useState("");
    const [search, setSearch] = useState("");
    const [stats, setStats] = useState({ total: 0, answered: 0, missed: 0, notAnswered: 0, avgDur: 0 });
    const [dlg, setDlg] = useState({ open: false, lead: null, person: null });

    useEffect(() => {
        const t = setTimeout(() => setSearch(qi.trim()), 400);
        return () => clearTimeout(t);
    }, [qi]);

    useEffect(() => { dispatch(actions.activeMenu("calllogs")); }, [dispatch]);

    /*
     * applyVis — scopes any Supabase query to the correct userId set.
     *   isRootUser → no filter (sees everything)
     *   visibleSmIds has values → .in("userId", visibleSmIds)
     *   otherwise → return null (caller will bail out and show empty)
     */
    const applyVis = useCallback(query => {
        // If role config says "All" — no filter, sees everything
        if (callLogsShowMode === "All") return query;

        // Otherwise — existing tree-based scoping
        if (isRootUser) return query;
        if (visibleSmIds?.length > 0) return query.in("userId", visibleSmIds);
        return null;
    }, [isRootUser, visibleSmIds, callLogsShowMode]);

    /* ── fetchLogs ──────────────────────────────────────────────────────── */
    const fetchLogs = useCallback(async (page, sv, reset = false) => {
        if (visibleSmIds === null) return;
        setLogsLoading(true);
        try {
            let q = supabase.from("CallLogs").select("*")
                .order("created_at", { ascending: false })
                .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

            q = applyVis(q);
            if (!q) { setLogs(reset ? [] : p => p); setLogsHasMore(false); return; }

            // SM filter
            if (activeFilters.sm?.length > 0) q = q.in("userId", activeFilters.sm);
            // Status filter
            if (activeFilters.status?.length > 0) q = q.in("callStatus", activeFilters.status);
            // Contact Type filter (calllogs.contactType)
            if (activeFilters.contactType?.length > 0) q = q.in("contactType", activeFilters.contactType);
            // Project filter: resolve via Leads table first
            if (activeFilters.project?.length > 0) {
                const { data: matchedLeads } = await supabase
                    .from("Leads").select("leadId").in("projectId", activeFilters.project);
                const leadIds = (matchedLeads || []).map(l => l.leadId);
                if (leadIds.length === 0) { setLogs(reset ? [] : p => p); setLogsHasMore(false); return; }
                q = q.in("leadId", leadIds);
            }
            // DATE FILTER
            if (activeFilters.date?.from) {
                const fromTs = new Date(`${activeFilters.date.from}T00:00:00`).getTime();
                q = q.gte("startedAt", fromTs);
            }
            if (activeFilters.date?.to) {
                const toTs = new Date(`${activeFilters.date.to}T23:59:59`).getTime();
                q = q.lte("startedAt", toTs);
            }

            if (sv) q = q.or(`customerName.ilike.%${sv}%,customerNumber.ilike.%${sv}%`);

            const { data, error } = await q;
            if (error) throw error;
            setLogs(prev => reset ? (data || []) : [...prev, ...(data || [])]);
            setLogsHasMore((data || []).length === PAGE_SIZE);
        } catch (e) { console.error(e); } finally { setLogsLoading(false); }
    }, [visibleSmIds, applyVis, activeFilters]);

    /* ── fetchLeads ─────────────────────────────────────────────────────── */
    const fetchLeads = useCallback(async (page, sv, reset = false) => {
        if (visibleSmIds === null) return;
        setLeadsLoading(true);
        try {
            let q = supabase.from("Leads").select(`
                leadId, leadDisplayId, leadType, created_at, projectHead,
                Person(personId, name, contactNumber, alternateNumber),
                Users:userId(userId, name),
                LeadStatus(leadStatusId, status, created_at)
            `).order("created_at", { ascending: false })
                .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

            q = applyVis(q);
            if (!q) { setLeads(reset ? [] : p => p); setLeadsHasMore(false); return; }

            // SM filter
            if (activeFilters.sm?.length > 0) q = q.in("userId", activeFilters.sm);
            // Project filter
            if (activeFilters.project?.length > 0) q = q.in("projectHead", activeFilters.project);

            const { data, error } = await q;
            if (error) throw error;

            let filtered = (data || []).map(l => {
                const s = l.LeadStatus || [];
                const latest = s.length ? s.reduce((a, b) => a.created_at > b.created_at ? a : b) : null;
                return {
                    ...l,
                    resolvedLeadStatus: latest?.status ?? null,
                    projectName: projectOptions.find(p => p.projectId === l.projectHead)?.projectName || null,
                };
            });

            // Client-side lead status filter (resolved from LeadStatus join)
            if (activeFilters.status?.length > 0) {
                filtered = filtered.filter(l => activeFilters.status.includes(String(l.resolvedLeadStatus)));
            }

            // Client-side search
            if (sv) {
                const low = sv.toLowerCase();
                filtered = filtered.filter(l =>
                    l.Person?.name?.toLowerCase().includes(low) ||
                    String(l.Person?.contactNumber || "").includes(low) ||
                    l.leadDisplayId?.toLowerCase().includes(low)
                );
            }

            setLeads(prev => reset ? filtered : [...prev, ...filtered]);
            setLeadsHasMore((data || []).length === PAGE_SIZE);
        } catch (e) { console.error(e); } finally { setLeadsLoading(false); }
    }, [visibleSmIds, applyVis, activeFilters, projectOptions]);

    /* ── Stats ──────────────────────────────────────────────────────────── */
    useEffect(() => {
        if (visibleSmIds === null) return;

        (async () => {
            let q = supabase
                .from("CallLogs")
                .select("callStatus,durationSec", { count: "exact" });

            q = applyVis(q);
            if (!q) {
                setStats({ total: 0, answered: 0, missed: 0, notAnswered: 0, avgDur: 0 });
                return;
            }

            // ✅ SM filter
            if (activeFilters.sm?.length > 0) {
                q = q.in("userId", activeFilters.sm);
            }

            // ✅ Status filter
            if (activeFilters.status?.length > 0) {
                q = q.in("callStatus", activeFilters.status);
            }

            // ✅ Contact Type filter (calllogs.contactType)
            if (activeFilters.contactType?.length > 0) {
                q = q.in("contactType", activeFilters.contactType);
            }

            // ✅ Project filter
            if (activeFilters.project?.length > 0) {
                const { data: matchedLeads } = await supabase
                    .from("Leads")
                    .select("leadId")
                    .in("projectId", activeFilters.project);

                const leadIds = (matchedLeads || []).map(l => l.leadId);

                if (leadIds.length === 0) {
                    setStats({ total: 0, answered: 0, missed: 0, notAnswered: 0, avgDur: 0 });
                    return;
                }

                q = q.in("leadId", leadIds);
            }

            // ✅ DATE FILTER (IMPORTANT - same as logs)
            if (activeFilters.date?.from) {
                const fromTs = new Date(`${activeFilters.date.from}T00:00:00`).getTime();
                q = q.gte("startedAt", fromTs);
            }

            if (activeFilters.date?.to) {
                const toTs = new Date(`${activeFilters.date.to}T23:59:59`).getTime();
                q = q.lte("startedAt", toTs);
            }

            const { data, count } = await q;

            if (data) {
                const ans = data.filter(d => d.callStatus === "answered").length;
                const mis = data.filter(d => d.callStatus === "missed").length;
                const notans = data.filter(d => d.callStatus === "not answered").length;
                const durs = data.map(d => d.durationSec || 0).filter(Boolean);

                setStats({
                    total: count || data.length,
                    answered: ans,
                    missed: mis,
                    notAnswered: notans,
                    avgDur: durs.length
                        ? Math.round(durs.reduce((a, b) => a + b, 0) / durs.length)
                        : 0,
                });
            }
        })();
    }, [visibleSmIds, applyVis, activeFilters]);

    /* ── Reset on tab / search / hierarchy / filter change ─────────────── */
    useEffect(() => {
        if (visibleSmIds === null) return;
        if (tab === 0) { setLogs([]); setLogsPage(0); setLogsHasMore(true); fetchLogs(0, search, true); }
        else if (tab === 1) { setLeads([]); setLeadsPage(0); setLeadsHasMore(true); fetchLeads(0, search, true); }
        // tabs 2/3/4 (Contacts, CP, FOS) manage their own fetching internally
    }, [tab, search, visibleSmIds, activeFilters]);

    /* ── Infinite scroll — logs ─────────────────────────────────────────── */
    useEffect(() => {
        if (tab !== 0) return;
        if (logsObs.current) logsObs.current.disconnect();
        logsObs.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && logsHasMore && !logsLoading) {
                const n = logsPage + 1; setLogsPage(n); fetchLogs(n, search);
            }
        }, { threshold: .1 });
        if (logsSentinel.current) logsObs.current.observe(logsSentinel.current);
        return () => logsObs.current?.disconnect();
    }, [tab, logsHasMore, logsLoading, logsPage, search, fetchLogs]);

    /* ── Infinite scroll — leads ────────────────────────────────────────── */
    useEffect(() => {
        if (tab !== 1) return;
        if (leadsObs.current) leadsObs.current.disconnect();
        leadsObs.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && leadsHasMore && !leadsLoading) {
                const n = leadsPage + 1; setLeadsPage(n); fetchLeads(n, search);
            }
        }, { threshold: .1 });
        if (leadsSentinel.current) leadsObs.current.observe(leadsSentinel.current);
        return () => leadsObs.current?.disconnect();
    }, [tab, leadsHasMore, leadsLoading, leadsPage, search, fetchLeads]);

    /* ── Handlers ───────────────────────────────────────────────────────── */
    const clearSearch = () => { setQi(""); setSearch(""); };
    const refresh = () => {
        if (tab === 0) { setLogs([]); setLogsPage(0); setLogsHasMore(true); fetchLogs(0, search, true); }
        else if (tab === 1) { setLeads([]); setLeadsPage(0); setLeadsHasMore(true); fetchLeads(0, search, true); }
        // tab === 2 refreshes via ContactsTab's internal state
    };

    const handleCallback = log => setDlg({
        open: true,
        lead: { leadId: log.leadId || null, leadDisplayId: log.leadDisplayId || "—" },
        person: { name: log.customerName || "Unknown", contactNumber: log.customerNumber, alternateNumber: null },
    });
    const handleCallNow = (lead, person) => setDlg({ open: true, lead, person });
    const handleConfirm = async (lead, person, number) => {
        setDlg({ open: false, lead: null, person: null });
        const payload = { agent_number: agentMobile, destination_number: number, caller_id: callerId };
        if (!payload.agent_number || !payload.destination_number) { toast.error("Missing agent or customer number"); return; }
        try {
            const res = await makeCall(payload);
            if (res?.success) toast.success(`Calling ${person?.name || "Lead"}`);
            else toast.error(res?.message || "Call failed");
        } catch (err) { toast.error(err?.response?.data?.message || err?.message || "Error initiating call"); }
    };

    /* ── Derived UI values ──────────────────────────────────────────────── */
    const totalActiveFilters = Object.values(activeFilters).flat().length;
    const hierarchyLoading = visibleSmIds === null;
    const isLoading = tab === 0 ? logsLoading : tab === 1 ? leadsLoading : false; // tabs 2-4 self-manage
    const ansRate = stats.total ? Math.round((stats.answered / stats.total) * 100) : 0;

    // Subtitle: purely positional, zero role-name strings
    const scopeLabel = isRootUser ? "All agents'" : isLeafUser ? "Your" : "Your team's";

    const STAT_CARDS = [
        { label: "Total Calls", value: stats.total, sub: `${ansRate}% answer rate`, color: C.indigo, soft: C.indigoSoft, Icon: <PhoneEnabled sx={{ fontSize: 17 }} /> },
        { label: "Answered", value: stats.answered, sub: "Successful", color: C.green, soft: C.greenSoft, Icon: <CheckCircle sx={{ fontSize: 17 }} /> },
        { label: "Missed", value: stats.missed, sub: "Need follow-up", color: C.red, soft: C.redSoft, Icon: <PhoneMissed sx={{ fontSize: 17 }} /> },
        { label: "Not Answered", value: stats.notAnswered, sub: "By customer", color: C.amber, soft: C.amberSoft, Icon: <PhoneMissed sx={{ fontSize: 17 }} /> },
        { label: "Avg Talk Time", value: fmtDur(stats.avgDur), sub: "Per answered call", color: C.sky, soft: C.skySoft, Icon: <AccessTime sx={{ fontSize: 17 }} /> },
    ];


    const getInterest = () => {
        try {
            const raw = aiModal.data.aiRawResponse;
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            return parsed?.analysis?.interest || null;
        } catch {
            return null;
        }
    };

    const interest = getInterest();
    const isMobile = useMediaQuery("(max-width:600px)");
    /* ── Render ─────────────────────────────────────────────────────────── */
    return (
        <>
            <style>{GLOBAL_CSS}</style>
            <Page>

                {/* PAGE HEADER */}
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: { xs: 2, sm: 2.5 }, flexWrap: "wrap", gap: 1.5 }}>
                    <Box>
                        <Typography sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.txt1, letterSpacing: "-.4px" }}>
                            <Box sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
                                <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", background: C.green, opacity: 0.6, animation: "ping 2s ease-out infinite" }} />
                                <Box sx={{ position: "relative", width: 8, height: 8, borderRadius: "50%", background: C.green }} />
                            </Box>
                            Call Logs
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: C.txt3, mt: 0.2 }}>
                            {scopeLabel} calls &amp; leads
                        </Typography>
                    </Box>
                </Box>

                {/* TOP TABS */}
                <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
                    <TopTabs value={viewTab} onChange={(_, v) => setViewTab(v)}>
                        <Tab label={<Stack direction="row" spacing={0.75} alignItems="center"><PhoneInTalk sx={{ fontSize: 15 }} /><span>Call Logs</span></Stack>} />
                        <Tab label={<Stack direction="row" spacing={0.75} alignItems="center"><TrendingUp sx={{ fontSize: 15 }} /><span>Dashboard</span></Stack>} />
                    </TopTabs>
                </Box>

                {/* DASHBOARD */}
                {viewTab === 1 && (
                    <CallLogsDashboard
                        visibleSmIds={visibleSmIds}
                        isSuperRole={isRootUser}
                        isSalesManager={isLeafUser}
                        callLogsShowMode={callLogsShowMode}
                        loggedInUserId={loggedInUserId}
                        smOptions={smOptions}
                        projectOptions={projectOptions}
                    />
                )}

                {/* CALL LOGS VIEW */}
                {viewTab === 0 && <>

                    {/* STAT CARDS */}
                    <Grid container spacing={{ xs: 0.75, sm: 1.5 }} sx={{ mb: { xs: 2, sm: 2.5 } }}>
                       {STAT_CARDS.map((s, i) => (
    <Grid
        item
        xs={i === STAT_CARDS.length - 1 ? 12 : 6}  // 👈 last card full width
        sm={4}
        md={2.4}
        key={s.label}
    >
        <Surface
            elevation={0}
            sx={{
                p: { xs: "10px 12px", sm: "16px 18px" },
                borderTop: `3px solid ${s.color}`,
                transition: "transform .15s, box-shadow .15s",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,.08)"
                }
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: { xs: 18, sm: 28 },
                            fontWeight: 800,
                            color: C.txt1,
                            fontFamily: C.mono,
                            letterSpacing: "-1px",
                            lineHeight: 1
                        }}
                    >
                        {hierarchyLoading ? "—" : s.value}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: 10.5, sm: 11.5 },
                            fontWeight: 600,
                            color: C.txt1,
                            mt: 0.6,
                            mb: 0.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {s.label}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: 10, sm: 11 },
                            color: C.txt4,
                            display: { xs: "none", sm: "block" }
                        }}
                    >
                        {s.sub}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        width: { xs: 28, sm: 36 },
                        height: { xs: 28, sm: 36 },
                        borderRadius: { xs: 7, sm: 9 },
                        background: s.soft,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: s.color,
                        flexShrink: 0
                    }}
                >
                    {s.Icon}
                </Box>
            </Stack>
        </Surface>
    </Grid>
))}
                    </Grid>

                    {/* SEARCH + FILTER ROW */}
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", flexWrap: "wrap", gap: { xs: 1, sm: 1.5 }, mb: { xs: 1.25, sm: 1.5 }, px: 0.25 }}>
                        {/* Legend */}
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ fontSize: 10, color: C.txt4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".03em" }}>Status:</Typography>
                            {Object.values(STATUS_MAP).map(s => (
                                <Stack key={s.label} direction="row" spacing={0.5} alignItems="center">
                                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: s.color }} />
                                    <Typography sx={{ fontSize: 11.5, color: C.txt2, fontWeight: 500 }}>{s.label}</Typography>
                                </Stack>
                            ))}
                        </Stack>

                        {/* Search + Filter + Refresh */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <SearchBox size="small" placeholder="Search name or number…" value={qi} onChange={e => setQi(e.target.value)}
                                sx={{ flex: 1, minWidth: { sm: 260 }, maxWidth: { sm: 340 } }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: C.txt4 }} /></InputAdornment>,
                                    endAdornment: qi ? <InputAdornment position="end"><IconButton size="small" onClick={clearSearch}><Close sx={{ fontSize: 13, color: C.txt4 }} /></IconButton></InputAdornment> : null,
                                }}
                            />
                            <Tooltip title="Filter">
                                <Box sx={{ position: "relative", flexShrink: 0 }}>
                                    <IconButton onClick={e => setFilterAnchorEl(e.currentTarget)}
                                        sx={{ border: `1px solid ${totalActiveFilters > 0 ? C.indigo : C.border}`, borderRadius: 8, color: totalActiveFilters > 0 ? C.indigo : C.txt3, background: totalActiveFilters > 0 ? C.indigoSoft : C.white, "&:hover": { background: C.indigoSoft, borderColor: C.indigo, color: C.indigo }, transition: "all .15s" }}>
                                        <FilterList sx={{ fontSize: 17 }} />
                                    </IconButton>
                                    {/* {totalActiveFilters > 0 && (
                                        <Box sx={{ position: "absolute", top: -5, right: -5, width: 17, height: 17, borderRadius: "50%", background: C.indigo, color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${C.page}`, fontFamily: C.sans }}>
                                            {totalActiveFilters}
                                        </Box>
                                    )} */}
                                </Box>
                            </Tooltip>
                            <Tooltip title="Refresh">
                                <IconButton onClick={refresh} disabled={isLoading || hierarchyLoading}
                                    sx={{ border: `1px solid ${C.border}`, borderRadius: 8, color: C.indigo, background: C.white, flexShrink: 0, "&:hover": { background: C.indigoSoft } }}>
                                    <Refresh sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>

                    {/* ACTIVE FILTER CHIPS */}
                    {totalActiveFilters > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25, px: 0.25 }}>
                            {(activeFilters.sm || []).map(id => {
                                const sm = smOptions.find(s => s.userId === id);
                                return (
                                    <Chip key={id} label={sm?.name || id} size="small"
                                        icon={<BusinessCenter sx={{ fontSize: "12px !important" }} />}
                                        onDelete={() => setActiveFilters(p => ({ ...p, sm: p.sm.filter(v => v !== id) }))}
                                        sx={{ fontSize: 11.5, fontFamily: C.sans, fontWeight: 500, background: C.indigoSoft, color: C.indigo, border: `1px solid ${C.indigoBd}`, "& .MuiChip-deleteIcon": { color: C.indigo, fontSize: 14 }, "& .MuiChip-icon": { color: C.indigo } }}
                                    />
                                );
                            })}
                            {(activeFilters.project || []).map(id => {
                                const proj = projectOptions.find(p => p.projectId === id);
                                return (
                                    <Chip key={id} label={proj?.projectName || id} size="small"
                                        icon={<FolderOpen sx={{ fontSize: "12px !important" }} />}
                                        onDelete={() => setActiveFilters(p => ({ ...p, project: p.project.filter(v => v !== id) }))}
                                        sx={{ fontSize: 11.5, fontFamily: C.sans, fontWeight: 500, background: alpha(C.sky, 0.1), color: C.sky, border: `1px solid ${alpha(C.sky, 0.25)}`, "& .MuiChip-deleteIcon": { color: C.sky, fontSize: 14 }, "& .MuiChip-icon": { color: C.sky } }}
                                    />
                                );
                            })}
                            {(activeFilters.status || []).map(val => {
                                const opt = [...CALL_STATUS_OPTIONS, ...LEAD_STATUS_OPTIONS].find(o => o.value === val);
                                return (
                                    <Chip key={val} label={opt?.label || val} size="small"
                                        onDelete={() => setActiveFilters(p => ({ ...p, status: p.status.filter(v => v !== val) }))}
                                        sx={{ fontSize: 11.5, fontFamily: C.sans, fontWeight: 500, background: alpha(opt?.color || C.txt4, 0.08), color: opt?.color || C.txt4, border: `1px solid ${alpha(opt?.color || C.txt4, 0.2)}`, "& .MuiChip-deleteIcon": { color: opt?.color || C.txt4, fontSize: 14 } }}
                                    />
                                );
                            })}
                            <Chip label="Clear all" size="small"
                                onClick={() => setActiveFilters({ sm: [], project: [], status: [], date: { from: null, to: null } })}
                                sx={{ fontSize: 11.5, fontFamily: C.sans, fontWeight: 500, background: C.page, color: C.txt3, border: `1px solid ${C.border}`, cursor: "pointer", "&:hover": { background: C.redSoft, color: C.red, borderColor: C.redBd } }}
                            />
                        </Box>
                    )}

                    {/* FEED SURFACE */}
                    <Surface elevation={0} sx={{ overflow: "hidden" }}>
                        {/* Pill tabs header */}
                        <Box sx={{ px: { xs: 1, sm: 1.5 }, py: 0.75, borderBottom: `1px solid ${C.border}` }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.75 }}>
                                {/* NOTE: tab switch no longer clears filters */}
                                <PillTabs
                                    value={tab}
                                    onChange={(_, v) => { setTab(v); setSearch(""); setQi(""); }}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    allowScrollButtonsMobile
                                    sx={{ maxWidth: "100%", "& .MuiTabScrollButton-root": { color: C.indigo, width: 20 } }}
                                >
                                    <Tab label={
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <PhoneInTalk sx={{ fontSize: 14 }} />
                                            <span>Call History</span>
                                            {!hierarchyLoading && (
                                                <Box sx={{ px: 0.8, py: 0.1, borderRadius: 10, fontSize: 10.5, fontWeight: 700, background: tab === 0 ? C.indigo : C.border2, color: tab === 0 ? "#fff" : C.txt4 }}>
                                                    {stats.total}
                                                </Box>
                                            )}
                                        </Stack>
                                    } />
                                    <Tab label={
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <PersonSearch sx={{ fontSize: 14 }} />
                                            <span>Leads</span>
                                        </Stack>
                                    } />
                                    <Tab label={
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <ContactPhone sx={{ fontSize: 14 }} />
                                            <span>Contacts</span>
                                        </Stack>
                                    } />
                                    <Tab label={
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <BusinessCenter sx={{ fontSize: 14 }} />
                                            <span>CP</span>
                                        </Stack>
                                    } />
                                    <Tab label={
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Groups sx={{ fontSize: 14 }} />
                                            <span>FOS</span>
                                        </Stack>
                                    } />
                                </PillTabs>
                                <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexShrink: 0 }}>
                                    <Box sx={{ position: "relative", width: 8, height: 8 }}>
                                        <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", background: C.green, opacity: .6, animation: "ping 2s ease-out infinite" }} />
                                        <Box sx={{ position: "relative", width: 8, height: 8, borderRadius: "50%", background: C.green }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: C.txt3, fontWeight: 600 }}>Live</Typography>
                                </Stack>
                            </Box>
                        </Box>

                        {/* Search banner */}
                        {search && (
                            <Box sx={{ px: { xs: 1.25, sm: 2 }, py: 0.75, background: alpha(C.indigo, .04), borderBottom: `1px solid ${C.border}` }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Search sx={{ fontSize: 13, color: C.indigo }} />
                                    <Typography sx={{ fontSize: 13, color: C.txt2 }}>Results for <strong>"{search}"</strong></Typography>
                                    <Box onClick={clearSearch} sx={{ px: 1, py: 0.15, borderRadius: 6, fontSize: 11, background: C.indigoSoft, color: C.indigo, cursor: "pointer", "&:hover": { background: C.indigoBd } }}>Clear</Box>
                                </Stack>
                            </Box>
                        )}

                        {(hierarchyLoading || (isLoading && (tab === 0 ? logs : leads).length === 0)) && (
                            <LinearProgress sx={{ height: 2, background: C.indigoSoft, "& .MuiLinearProgress-bar": { background: `linear-gradient(90deg,${C.indigo},${C.indigo2})` } }} />
                        )}

                        {/* Feed */}
                        <Box sx={{ p: { xs: 1.25, sm: 1.5 }, display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2 }, background: C.page }}>

                            {/* Call History */}
                            {tab === 0 && <>
                                {logs.length === 0 && !logsLoading && !hierarchyLoading && (
                                    <Box sx={{ py: 8, textAlign: "center" }}>
                                        <PhoneMissed sx={{ fontSize: 40, color: C.border, mb: 1.5 }} />
                                        <Typography sx={{ color: C.txt3, fontSize: 14, fontWeight: 500 }}>No call logs found</Typography>
                                        {(search || totalActiveFilters > 0) && <Typography sx={{ color: C.txt4, fontSize: 12.5, mt: 0.5 }}>Try adjusting your search or filters</Typography>}
                                    </Box>
                                )}
                                {logs.map((log, i) => (
                                    <CallRow
                                        key={log.id || i}
                                        log={log}
                                        onCallback={handleCallback}
                                        setAiModal={setAiModal}   // 👈 ADD THIS
                                    />
                                ))}
                                <div ref={logsSentinel} style={{ height: 1 }} />
                                {logsLoading && logs.length > 0 && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} sx={{ color: C.indigo }} /></Box>}
                                {!logsHasMore && logs.length > 0 && <Typography sx={{ textAlign: "center", fontSize: 11.5, color: C.txt4, py: 1.5, borderTop: `1px dashed ${C.border}`, mt: 0.5 }}>✓ All {logs.length} calls loaded</Typography>}
                            </>}

                            {/* Contacts */}
                            {tab === 1 && <>
                                {leads.length === 0 && !leadsLoading && !hierarchyLoading && (
                                    <Box sx={{ py: 8, textAlign: "center" }}>
                                        <ContactPhone sx={{ fontSize: 40, color: C.border, mb: 1.5 }} />
                                        <Typography sx={{ color: C.txt3, fontSize: 14, fontWeight: 500 }}>No leads found</Typography>
                                        {(search || totalActiveFilters > 0) && <Typography sx={{ color: C.txt4, fontSize: 12.5, mt: 0.5 }}>Try adjusting your search or filters</Typography>}
                                    </Box>
                                )}
                                {leads.map((lead, i) => <LeadRow key={lead.leadId || i} lead={lead} onCallNow={handleCallNow} />)}
                                <div ref={leadsSentinel} style={{ height: 1 }} />
                                {leadsLoading && leads.length > 0 && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} sx={{ color: C.green }} /></Box>}
                                {!leadsHasMore && leads.length > 0 && <Typography sx={{ textAlign: "center", fontSize: 11.5, color: C.txt4, py: 1.5, borderTop: `1px dashed ${C.border}`, mt: 0.5 }}>✓ All {leads.length} leads loaded</Typography>}
                            </>}

                            {tab === 2 && (
                                <ContactsTab userId={loggedInUserId} onCallNow={handleCallNow} />
                            )}

                            {tab === 3 && (
                                <CPTab onCallNow={handleCallNow} />
                            )}

                            {tab === 4 && (
                                <FOSTab onCallNow={handleCallNow} />
                            )}

                        </Box>
                    </Surface>

                    {/* Filter Panel */}
                    <FilterPanel
                        anchorEl={filterAnchorEl}
                        isLeafUser={isLeafUser}
                        onClose={() => setFilterAnchorEl(null)}
                        tab={tab}
                        filters={activeFilters}
                        onApply={filters => setActiveFilters(filters)}
                        smOptions={smOptions}
                        projectOptions={projectOptions}
                    />

                    {/* Call Now Dialog */}
                    <CallNowDialog
                        open={dlg.open} lead={dlg.lead} person={dlg.person}
                        onClose={() => setDlg({ open: false, lead: null, person: null })}
                        onConfirm={handleConfirm}
                    />
                    <Dialog
                        open={aiModal.open}
                        onClose={() => setAiModal({ open: false, data: null })}
                        maxWidth="md"
                        fullWidth
                        fullScreen={isMobile}
                        PaperProps={{
                            sx: {
                                borderRadius: isMobile ? 0 : 3,
                                overflow: "hidden",
                                boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
                                display: "flex",
                                flexDirection: "column",
                            }
                        }}
                    >
                        {/* ── HEADER ── */}
                        <DialogTitle sx={{
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1.5, sm: 2 },
                            background: C.white,
                            borderBottom: `1px solid ${C.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            flexShrink: 0,
                        }}>
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    width: { xs: 32, sm: 36 },
                                    height: { xs: 32, sm: 36 },
                                    borderRadius: "50%",
                                    background: C.indigoSoft,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}>
                                    <Psychology sx={{ fontSize: { xs: 16, sm: 18 }, color: C.indigo }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: 14, sm: 15 },
                                        color: C.txt1,
                                        fontFamily: C.sans,
                                        lineHeight: 1.2,
                                    }}>
                                        AI Call Analysis
                                    </Typography>
                                    {aiModal.data?.customerName && (
                                        <Typography sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            color: C.txt4,
                                            fontFamily: C.sans,
                                            mt: 0.2,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}>
                                            {aiModal.data.customerName}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                            <IconButton
                                onClick={() => setAiModal({ open: false, data: null })}
                                size="small"
                                sx={{
                                    flexShrink: 0,
                                    color: C.txt3,
                                    "&:hover": { color: C.txt1, background: C.border2 }
                                }}
                            >
                                <Close sx={{ fontSize: 20 }} />
                            </IconButton>
                        </DialogTitle>

                        {/* ── BODY ── */}
                        <DialogContent sx={{
                            px: { xs: 2, sm: 3 },
                            py: { xs: 2, sm: 2.5 },
                            background: C.page,
                            flex: 1,
                            overflowY: "auto",
                        }}>
                            {aiModal.data && (
                                <Stack spacing={{ xs: 1.75, sm: 2 }}>

                                    {/* TOP CHIPS */}
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 , pt: 2 }}>
                                        <Chip
                                            label={`Score  ${aiModal.data.aiLeadScore || 0}`}
                                            sx={{
                                                fontWeight: 700,
                                                fontFamily: C.sans,
                                                fontSize: { xs: 11.5, sm: 12 },
                                                borderRadius: 2,
                                                height: 28,
                                                background: scoreSoft(aiModal.data.aiLeadScore || 0),
                                                color: scoreColor(aiModal.data.aiLeadScore || 0),
                                                border: `1px solid ${scoreColor(aiModal.data.aiLeadScore || 0)}22`,
                                            }}
                                        />
                                        {aiModal.data.aiLeadTemperature && (
                                            <Chip
                                                label={aiModal.data.aiLeadTemperature}
                                                sx={{
                                                    fontWeight: 600,
                                                    fontFamily: C.sans,
                                                    fontSize: { xs: 11.5, sm: 12 },
                                                    borderRadius: 2,
                                                    height: 28,
                                                    background: C.amberSoft,
                                                    color: C.amber,
                                                    border: `1px solid ${C.amberBd}`,
                                                }}
                                            />
                                        )}
                                        {aiModal.data.aiCallType && (
                                            <Chip
                                                label={aiModal.data.aiCallType}
                                                sx={{
                                                    fontWeight: 600,
                                                    fontFamily: C.sans,
                                                    fontSize: { xs: 11.5, sm: 12 },
                                                    borderRadius: 2,
                                                    height: 28,
                                                    background: C.indigoSoft,
                                                    color: C.indigo,
                                                    border: `1px solid ${C.indigoBd}`,
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* CUSTOMER INTEREST */}
                                    {interest && (
                                        <Box sx={{
                                            background: C.white,
                                            border: `1px solid ${C.border}`,
                                            borderRadius: 2.5,
                                            p: { xs: 1.75, sm: 2.25 },
                                        }}>
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                                                <Box sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: "50%",
                                                    background: C.indigoSoft,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}>
                                                    <BusinessCenter sx={{ fontSize: 13, color: C.indigo }} />
                                                </Box>
                                                <Typography sx={{
                                                    fontSize: { xs: 10.5, sm: 11 },
                                                    fontWeight: 700,
                                                    color: C.indigo,
                                                    textTransform: "uppercase",
                                                    letterSpacing: ".08em",
                                                    fontFamily: C.sans,
                                                }}>
                                                    Customer Interest
                                                </Typography>
                                            </Stack>
                                            <Box sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(auto-fit, minmax(140px, 1fr))" },
                                                gap: { xs: 1, sm: 1.25 },
                                            }}>
                                                {interest.purpose && (
                                                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2, background: C.page, border: `1px solid ${C.border}` }}>
                                                        <Typography sx={{ fontSize: 10, color: C.txt4, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, mb: 0.4 }}>Purpose</Typography>
                                                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: C.txt1, lineHeight: 1.3 }}>{interest.purpose}</Typography>
                                                    </Box>
                                                )}
                                                {interest.budget_range && (
                                                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2, background: C.greenSoft, border: `1px solid ${C.greenBd}` }}>
                                                        <Typography sx={{ fontSize: 10, color: C.green, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, mb: 0.4 }}>Budget</Typography>
                                                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: C.txt1, lineHeight: 1.3 }}>{interest.budget_range}</Typography>
                                                    </Box>
                                                )}
                                                {interest.project_name && (
                                                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2, background: C.indigoSoft, border: `1px solid ${C.indigoBd}` }}>
                                                        <Typography sx={{ fontSize: 10, color: C.indigo, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, mb: 0.4 }}>Project</Typography>
                                                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: C.txt1, lineHeight: 1.3 }}>{interest.project_name}</Typography>
                                                    </Box>
                                                )}
                                                {interest.configuration && (
                                                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2, background: C.amberSoft, border: `1px solid ${C.amberBd}` }}>
                                                        <Typography sx={{ fontSize: 10, color: C.amber, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, mb: 0.4 }}>Config</Typography>
                                                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: C.txt1, lineHeight: 1.3 }}>{interest.configuration}</Typography>
                                                    </Box>
                                                )}
                                                {interest.preferred_floor && (
                                                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2, background: "#f5f3ff", border: "1px solid #ddd6fe" }}>
                                                        <Typography sx={{ fontSize: 10, color: "#7c3aed", textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, mb: 0.4 }}>Floor</Typography>
                                                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: C.txt1, lineHeight: 1.3 }}>{interest.preferred_floor}</Typography>
                                                    </Box>
                                                )}
                                                {interest.preferred_facing && (
                                                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2, background: C.skySoft, border: `1px solid ${C.skyBd}` }}>
                                                        <Typography sx={{ fontSize: 10, color: C.sky, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, mb: 0.4 }}>Facing</Typography>
                                                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: C.txt1, lineHeight: 1.3 }}>{interest.preferred_facing}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* QUICK SUMMARY */}
                                    <Box sx={{
                                        background: C.white,
                                        border: `1px solid ${C.border}`,
                                        borderRadius: 2.5,
                                        p: { xs: 1.75, sm: 2.25 },
                                    }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: { xs: 1, sm: 1.25 } }}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                background: C.indigoSoft,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}>
                                                <Psychology sx={{ fontSize: 13, color: C.indigo }} />
                                            </Box>
                                            <Typography sx={{
                                                fontSize: { xs: 10.5, sm: 11 },
                                                fontWeight: 700,
                                                color: C.indigo,
                                                textTransform: "uppercase",
                                                letterSpacing: ".08em",
                                                fontFamily: C.sans,
                                            }}>
                                                Quick Summary
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{
                                            fontSize: { xs: 12.5, sm: 13 },
                                            color: C.txt2,
                                            lineHeight: 1.7,
                                            fontFamily: C.sans,
                                        }}>
                                            {aiModal.data.aiShortSummary || "No data"}
                                        </Typography>
                                    </Box>

                                    {/* RECOMMENDED ACTION */}
                                    <Box sx={{
                                        background: C.greenSoft,
                                        border: `1px solid ${C.greenBd}`,
                                        borderRadius: 2.5,
                                        p: { xs: 1.75, sm: 2.25 },
                                    }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: { xs: 1, sm: 1.25 } }}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                background: "#dcfce7",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}>
                                                <TrendingUp sx={{ fontSize: 13, color: C.green }} />
                                            </Box>
                                            <Typography sx={{
                                                fontSize: { xs: 10.5, sm: 11 },
                                                fontWeight: 700,
                                                color: C.green,
                                                textTransform: "uppercase",
                                                letterSpacing: ".08em",
                                                fontFamily: C.sans,
                                            }}>
                                                Recommended Action
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{
                                            fontSize: { xs: 12.5, sm: 13 },
                                            color: "#065f46",
                                            fontWeight: 500,
                                            lineHeight: 1.7,
                                            fontFamily: C.sans,
                                        }}>
                                            {(() => {
                                                try {
                                                    const raw = aiModal.data.aiRawResponse;
                                                    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                                                    return parsed?.analysis?.recommended_next_action || "No recommendation";
                                                } catch {
                                                    return "Invalid AI data";
                                                }
                                            })()}
                                        </Typography>
                                    </Box>

                                    {/* DETAILED SUMMARY */}
                                    <Box sx={{
                                        background: C.white,
                                        border: `1px solid ${C.border}`,
                                        borderRadius: 2.5,
                                        p: { xs: 1.75, sm: 2.25 },
                                    }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: { xs: 1, sm: 1.25 } }}>
                                            <Box sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                background: C.border2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}>
                                                <Article sx={{ fontSize: 13, color: C.txt3 }} />
                                            </Box>
                                            <Typography sx={{
                                                fontSize: { xs: 10.5, sm: 11 },
                                                fontWeight: 700,
                                                color: C.txt3,
                                                textTransform: "uppercase",
                                                letterSpacing: ".08em",
                                                fontFamily: C.sans,
                                            }}>
                                                Detailed Summary
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{
                                            fontSize: { xs: 12.5, sm: 13 },
                                            color: C.txt2,
                                            lineHeight: 1.75,
                                            fontFamily: C.sans,
                                            wordBreak: "break-word",
                                        }}>
                                            {aiModal.data.aiDetailedSummary || "No data"}
                                        </Typography>
                                    </Box>

                                </Stack>
                            )}
                        </DialogContent>

                        {/* ── FOOTER ── */}
                        <DialogActions sx={{
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1.5, sm: 2 },
                            background: C.white,
                            borderTop: `1px solid ${C.border}`,
                            flexShrink: 0,
                        }}>
                            <Button
                                onClick={() => setAiModal({ open: false, data: null })}
                                fullWidth={isMobile}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontFamily: C.sans,
                                    fontSize: { xs: 13, sm: 13.5 },
                                    px: { xs: 2.5, sm: 3 },
                                    py: { xs: 1, sm: 0.875 },
                                    color: "#fff",
                                    background: C.indigo,
                                    boxShadow: "none",
                                    "&:hover": { background: C.indigo2, boxShadow: "none" },
                                }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                </>}
            </Page>
        </>
    );
}